import { colors } from '@lmc/common/constants'
import type { RouterProps } from '@lmc/common/types/router'
import type {
  CarePlan,
  CarePlanAdl,
  ContingencyPlanDTO,
  FormField,
  HomeFeatures,
  RiskAssessment
} from '@lmc/ts-common'
import { Modal, Text } from '@lmc/ui-kit'
import ConsentSection from 'components/consent/ConsentSection'
import { ContingencyPlanSelector } from 'components/generic/ContingencyPlanSelector/ContingencyPlanSelector'
import { NAMESPACE } from 'locale'
import { riskAssessmentFormConstants } from 'locale/riskAssessmentForm'
import _ from 'lodash'
import type React from 'react'
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { connect } from 'react-redux'
import RiskAssessmentEditFooter from 'screens/Residents/screens/ResidentRiskAssessmentsScreens/components/RiskAssessmentEditFooter'
import styled from 'styled-components'
import Actions from '../../../../actions'
import { getNominative } from '../../../../common/utils'
import Operations from '../../../../operations'
import Selectors from '../../../../selectors'
import { withRouter } from '../../../../withRouter'
import Field from '../../../fields'
import {
  CarePlanSelector,
  ErrorBox,
  IconSectionHeader,
  Spinner
} from '../../../generic'
import { ImageSection } from '../../../generic/ListItem/components'
import {
  AssessmentEditableHeader,
  type FormProps
} from '../../../modals/CreateRiskAssessment/components'
import Form from '../..'
import FormFields from '../../FormFields'
import RiskAssessmentOutcome from './RiskAssessmentOutcome'

interface FormTemplate {
  id: string
  name: string
  iconText?: string
  color?: string
  hasRiskScore?: boolean
  hasActions?: boolean
  formFields: FormField[]
  carePlanAdl?: CarePlanAdl
}

interface FormData {
  carePlanAdl: string | null
  name?: string | null
  [key: string]: any
}

interface CarePlanAdlExtended extends Omit<CarePlanAdl, 'icon'> {
  noFurtherSupportRequired: boolean
  value: any
  carePlan?: CarePlan
  icon: string
  iconText: string
}

type ContingencyPlanItem = ContingencyPlanDTO | string

const getContingencyPlanId = (plan: ContingencyPlanItem) =>
  typeof plan === 'string' ? plan : plan.id

export interface RefactoredRiskAssessmentFormProps {
  fetchList: (list: string) => void
  carePlanAdls: CarePlanAdlExtended[]
  loadListItem: (list: string, itemId: string) => void
  form?: FormTemplate
  form_id: string
  fetchCarePlan: (id: string) => void
  carePlansDefault: CarePlanAdlExtended[]
  contingencyPlansDefault?: ContingencyPlanItem[]
  consentFormsDefault?: string[]
  linkedContingencyPlanId?: string
  features: HomeFeatures
  router: RouterProps<{
    id: string
    form_id?: string
    risk_assessment_id?: string
  }>
  addForm: (initial?: any) => void
  customName?: string
  formData: FormData
  setFormField: (data: { key: string; value: any }) => void
  fields: Record<string, any>
  carePlans?: {
    adls: CarePlanAdlExtended[]
    carePlanSortType: number | null
    historyDateRange: {
      startDate: Date
      endDate: Date
    }
    riskAssessmentSortType: number | null
  }
  carePlanNominative: string
  resident?: {
    name: {
      first: string
      last: string
    }
    dateOfBirth: string
    picture: string
    status: string
  }
  risk: {
    level: 'na' | 'low' | 'medium' | 'high' | 'severe' | 'incomplete'
    score: number
  }
  createItem: (data: any) => void
  onCancel: () => void
  onComplete: (result: any) => void
  initial: Record<string, any>
  riskAssessmentId?: string
  updateItem: (data: any) => void
  errors: any
  archived: boolean
  style?: React.CSSProperties
  isRiskAssessmentFuturePlanEnabled?: boolean
  isConsentRiskLinkEnabled?: boolean
}

const HeaderInner = styled.div<{ $hasIconText: boolean }>`
  margin-left: ${(props) => (props.$hasIconText ? 0 : 55)}px;
  margin-bottom: 24px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-left: 0;
  }
`

const StyledErrorBox = styled(ErrorBox)`
  margin-left: 55px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-left: 0;
  }
`

const SectionContainer = styled.div``

const TitleContainer = styled.div`
  margin-top: 55px;
`

const FieldContainer = styled.div`
  padding-top: 24px;
  margin-left: 0;
`

const ModalNoteText = styled(Text)`
  color: ${colors.red};
`

const ModalDescriptionText = styled(Text)`
  margin-bottom: 20px;
`

const ModalCarePlanList = styled.div`
  margin-top: 10px;
`

const CarePlanItem = styled.div`
  display: flex;
`

const CarePlanItemText = styled(Text)`
  margin-left: 5px;
`

const ConsentSectionWrapper = styled.div`
  margin-top: 24px;
`

export const RefactoredRiskAssessmentForm = (
  props: RefactoredRiskAssessmentFormProps
) => {
  const {
    fetchList,
    carePlanAdls,
    loadListItem,
    form,
    form_id,
    fetchCarePlan,
    carePlansDefault,
    contingencyPlansDefault,
    consentFormsDefault,
    linkedContingencyPlanId,
    features,
    router,
    addForm,
    customName,
    formData,
    setFormField,
    fields,
    carePlans,
    carePlanNominative,
    resident,
    risk,
    createItem,
    onCancel,
    onComplete,
    initial,
    riskAssessmentId,
    updateItem,
    errors,
    archived,
    style,
    isRiskAssessmentFuturePlanEnabled = false,
    isConsentRiskLinkEnabled = false
  } = props
  const [carePlansState, setCarePlansState] = useState<CarePlanAdlExtended[]>(
    []
  )
  const [carePlansDefaultState, setCarePlansDefaultState] = useState<
    CarePlanAdlExtended[]
  >([])
  const [contingencyPlansState, setContingencyPlansState] = useState<
    ContingencyPlanItem[]
  >([])
  const [contingencyPlansDefaultState, setContingencyPlansDefaultState] =
    useState<ContingencyPlanItem[]>([])
  const [consentFormsState, setConsentFormsState] = useState<string[]>([])
  const [consentFormsDefaultState, setConsentFormsDefaultState] = useState<
    string[]
  >([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const formRef = useRef<HTMLDivElement>(null)
  const hasInitializedForm = useRef(false)
  const hasInitializedCarePlans = useRef(false)
  const hasInitializedContingencyPlans = useRef(false)
  const hasInitializedConsentForms = useRef(false)

  const { t } = useTranslation(NAMESPACE.RISK_ASSESSMENTS_FORM)
  const queryClient = useQueryClient()

  // Initialize form on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!carePlanAdls) {
      fetchList('care-plan-adls')
    }

    loadListItem('forms', form_id)

    const id = router?.params?.id

    if (features && id) {
      fetchCarePlan(id)
    }
  }, [])

  // Initialize care plans from defaults (only once when carePlansDefault is available)
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (carePlansDefault && !hasInitializedCarePlans.current) {
      const carePlansDefaultWithSupport = carePlansDefault.filter(
        (carePlan) => !carePlan.noFurtherSupportRequired
      )
      if (carePlansDefaultWithSupport.length > 0) {
        hasInitializedCarePlans.current = true
        setCarePlansState(carePlansDefaultWithSupport)
        setCarePlansDefaultState(carePlansDefaultWithSupport)
      }
    }
  }, [carePlansDefault])

  // Initialize future plans from defaults (only once when contingencyPlansDefault is available)
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (
      isRiskAssessmentFuturePlanEnabled &&
      contingencyPlansDefault &&
      contingencyPlansDefault.length > 0 &&
      !hasInitializedContingencyPlans.current
    ) {
      hasInitializedContingencyPlans.current = true
      setContingencyPlansState(contingencyPlansDefault)
      setContingencyPlansDefaultState(contingencyPlansDefault)
    }
  }, [contingencyPlansDefault, isRiskAssessmentFuturePlanEnabled])

  useEffect(() => {
    if (
      isConsentRiskLinkEnabled &&
      consentFormsDefault &&
      consentFormsDefault.length > 0 &&
      !hasInitializedConsentForms.current
    ) {
      hasInitializedConsentForms.current = true
      setConsentFormsState(consentFormsDefault)
      setConsentFormsDefaultState(consentFormsDefault)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentFormsDefault, isConsentRiskLinkEnabled])

  // Add form when available (only once)
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (form && !hasInitializedForm.current) {
      hasInitializedForm.current = true
      addForm({
        ...formData,
        carePlanAdl: form.carePlanAdl || null
      })
      setFormField({ key: 'name', value: customName || null })
    }
  }, [form])

  const hasChangedCarePlans = useCallback(() => {
    // Create sorted copies without mutating original arrays
    const defaultSorted = [...(carePlansDefaultState || [])].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    )
    const currentSorted = [...(carePlansState || [])].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    )
    return JSON.stringify(defaultSorted) !== JSON.stringify(currentSorted)
  }, [carePlansDefaultState, carePlansState])

  const hasChangedContingencyPlans = useCallback(() => {
    if (!isRiskAssessmentFuturePlanEnabled) return false

    const defaultIds = [...(contingencyPlansDefaultState || [])]
      .map(getContingencyPlanId)
      .sort((a, b) => a.localeCompare(b))
    const currentIds = [...(contingencyPlansState || [])]
      .map(getContingencyPlanId)
      .sort((a, b) => a.localeCompare(b))

    return JSON.stringify(defaultIds) !== JSON.stringify(currentIds)
  }, [
    contingencyPlansDefaultState,
    contingencyPlansState,
    isRiskAssessmentFuturePlanEnabled
  ])

  const hasChangedConsentForms = useCallback(() => {
    if (!isConsentRiskLinkEnabled) return false

    const defaultIds = [...consentFormsDefaultState].sort((a, b) =>
      a.localeCompare(b)
    )
    const currentIds = [...consentFormsState].sort((a, b) => a.localeCompare(b))

    return JSON.stringify(defaultIds) !== JSON.stringify(currentIds)
  }, [consentFormsDefaultState, consentFormsState, isConsentRiskLinkEnabled])

  const handleSubmit = useCallback(() => {
    const { params } = router
    const contingencyPlansPayload =
      isRiskAssessmentFuturePlanEnabled || !!linkedContingencyPlanId
        ? Array.from(
            new Set([
              ...(contingencyPlansState?.map(getContingencyPlanId) || []),
              ...(linkedContingencyPlanId ? [linkedContingencyPlanId] : [])
            ])
          )
        : undefined

    // Only include consentForms in the payload when the user actually changed
    // them, so unrelated saves don't risk overwriting server-side links if our
    // local state never picked them up (e.g. response missing the field).
    const consentFormsPayload =
      isConsentRiskLinkEnabled && hasChangedConsentForms()
        ? consentFormsState
        : undefined

    if (
      !_.isEqual(fields, initial) ||
      hasChangedCarePlans() ||
      hasChangedContingencyPlans() ||
      hasChangedConsentForms() ||
      !!linkedContingencyPlanId
    ) {
      const chosenId = form_id || params.form_id

      const name = fields?.name
        ? `${form?.name} for ${fields?.name}`
        : undefined

      const carePlansChoosen = !!carePlansState.length

      createItem({
        url: `list/risk-assessments`,
        json: {
          form: chosenId,
          formData: fields,
          name,
          resident: params.id,
          riskLevel: risk.level,
          riskScore: risk.score,
          carePlans: carePlansChoosen
            ? carePlansState?.map((item) => item?.value || item?.id)
            : [],
          contingencyPlans: contingencyPlansPayload,
          ...(consentFormsPayload !== undefined
            ? { consentForms: consentFormsPayload }
            : {})
        },
        formId: FORM_ID,
        onError: (e: Error) => {
          console.log('onError', e)
          formRef.current?.scrollIntoView({ behavior: 'smooth' })
        },
        onComplete: (res: RiskAssessment) => {
          queryClient.invalidateQueries(['risk-assessment', res.id])
          if (!params?.risk_assessment_id) {
            onComplete(res)
          } else {
            router.navigate(
              `/residents/${params.id}/risk-assessments/${res.id}`,
              { replace: true }
            )
          }
        }
      })
    } else if (riskAssessmentId) {
      updateItem({
        url: `list/risk-assessments/${riskAssessmentId}`,
        ...(consentFormsPayload !== undefined
          ? { json: { consentForms: consentFormsPayload } }
          : {}),
        formId: FORM_ID,
        onError: (e: Error) => {
          console.log('onError', e)
          formRef.current?.scrollIntoView({ behavior: 'smooth' })
        },
        onComplete: (res: RiskAssessment) => {
          queryClient.invalidateQueries(['risk-assessment', res.id])
          if (!params?.risk_assessment_id) {
            onComplete(res)
          } else {
            router.navigate(
              `/residents/${params.id}/risk-assessments/${res.id}`,
              { replace: true }
            )
          }
        }
      })
    } else {
      onCancel()
    }
  }, [
    fields,
    initial,
    hasChangedCarePlans,
    hasChangedContingencyPlans,
    hasChangedConsentForms,
    carePlansState,
    contingencyPlansState,
    consentFormsState,
    form_id,
    router,
    risk,
    createItem,
    updateItem,
    riskAssessmentId,
    onComplete,
    onCancel,
    form,
    isRiskAssessmentFuturePlanEnabled,
    isConsentRiskLinkEnabled,
    linkedContingencyPlanId,
    queryClient
  ])

  const setCustomName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormField({
        key: 'name',
        value: e.target.value
      })
    },
    [setFormField]
  )

  const handleCarePlanIds = useCallback((value: CarePlanAdlExtended[]) => {
    setCarePlansState(value)
  }, [])

  const handleContingencyPlanIds = useCallback(
    (value: ContingencyPlanDTO[]) => {
      setContingencyPlansState(value)
    },
    []
  )

  const handleConsentFormIds = useCallback((value: string[]) => {
    setConsentFormsState(value)
  }, [])

  const renderFields = () => {
    if (!form || !form.formFields) return null

    // Check if there are any care plan options available (for warning logic)
    const hasCarePlanOptions = (carePlans?.adls || carePlanAdls || [])?.some(
      (item) =>
        item?.carePlan &&
        !item?.carePlan?.noFurtherSupportRequired &&
        item?.carePlan?.nextReviewDate
    )

    // Determine warning message based on state
    const hasCarePlansSelected = carePlansState?.length > 0

    let warningMessage: string | undefined

    if (hasCarePlanOptions && !hasCarePlansSelected) {
      warningMessage = t(riskAssessmentFormConstants.PASSPORT_NOTE, {
        carePlanNominative: getNominative(
          carePlanNominative,
          riskAssessmentFormConstants.CARE_PLAN
        )
      })
    } else if (!hasCarePlanOptions && !hasCarePlansSelected) {
      warningMessage = t(riskAssessmentFormConstants.REVIEW_CARE_PLAN_NOTE, {
        carePlanNominative: getNominative(
          carePlanNominative,
          riskAssessmentFormConstants.CARE_PLAN
        )
      })
    }

    return (
      <>
        <CarePlanSelector
          residentId={router.params.id}
          selectedCarePlans={carePlansState}
          onChange={handleCarePlanIds}
          warningMessage={warningMessage}
        />
        {isRiskAssessmentFuturePlanEnabled && (
          <ContingencyPlanSelector
            residentId={router.params.id}
            selectedContingencyPlans={contingencyPlansState}
            onChange={handleContingencyPlanIds}
            label="Attached to Future Plan"
          />
        )}
        <FormFields
          form={form}
          fields={fields}
          readOnly={false}
          userGenerated
        />
      </>
    )
  }

  const renderConsentSection = () => {
    if (!isConsentRiskLinkEnabled) return null
    return (
      <ConsentSectionWrapper>
        <ConsentSection
          residentId={router.params.id}
          value={consentFormsState}
          onChange={handleConsentFormIds}
          disableItemNavigation
        />
      </ConsentSectionWrapper>
    )
  }

  const renderOutcomesScore = () => {
    if (form && !form.hasRiskScore) return null
    const riskOutcomeScoreTranslation = t(
      riskAssessmentFormConstants.RISK_OUTCOME_SCORE
    )
    return (
      <SectionContainer>
        <TitleContainer>
          <IconSectionHeader
            icon="info"
            title={riskOutcomeScoreTranslation}
            color={form?.color || colors.darkblue}
          />
        </TitleContainer>
        <FieldContainer>
          <RiskAssessmentOutcome
            hasActions={form?.hasActions}
            resident={resident!}
            risk={risk}
          />
        </FieldContainer>
      </SectionContainer>
    )
  }

  const renderActionsSection = () => {
    if (form && !form.hasActions) return null
    const actionsToMitigateTranslation = t(
      riskAssessmentFormConstants.ACTIONS_TO_MITIGATE
    )
    return (
      <SectionContainer>
        <TitleContainer>
          <IconSectionHeader
            icon="actions"
            title={actionsToMitigateTranslation}
            color={form?.color || colors.darkblue}
          />
        </TitleContainer>
        <FieldContainer>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              className="lmc-card-edit"
              keys="actions"
              formId={FORM_ID}
              note={getActionsNote(carePlanNominative, t)}
              layout="no-label"
              required
              type="textarea"
              values={fields}
            />
          </Suspense>
        </FieldContainer>
      </SectionContainer>
    )
  }

  const renderHeader = () => {
    return (
      <div>
        <HeaderInner $hasIconText={!!form?.iconText}>
          {fields && (fields.name || fields.name === '') ? (
            <AssessmentEditableHeader
              form={form as FormProps}
              hasToggle
              onChange={setCustomName}
              value={fields.name}
            />
          ) : (
            <IconSectionHeader
              isMainHeader
              title={form?.name}
              iconText={form?.iconText}
              color={form?.color}
            />
          )}
        </HeaderInner>
        <StyledErrorBox errors={errors} />
      </div>
    )
  }

  const renderModal = () => {
    const carePlansToDisplay = carePlansState.filter(
      (carePlan) => !carePlan.noFurtherSupportRequired
    )
    const noCarePlansToDisplay = carePlansState?.length
    const confirmTextTranslation = t(riskAssessmentFormConstants.CONFIRM_TEXT)
    const cancelTextTranslation = t(riskAssessmentFormConstants.CANCEL_TEXT)
    const pleaseNoteTranslation = t(riskAssessmentFormConstants.PLEASE_NOTE)
    const updatingRiskAssessmentTranslation = t(
      riskAssessmentFormConstants.UPDATING_RISK_ASSESSMENT,
      {
        carePlanNominative: getNominative(
          carePlanNominative,
          `${noCarePlansToDisplay > 1 ? riskAssessmentFormConstants.CARE_PLANS : riskAssessmentFormConstants.CARE_PLAN}`
        )
      }
    )

    return (
      <Modal
        localisation={{
          confirmText: confirmTextTranslation,
          cancelText: cancelTextTranslation
        }}
        onConfirm={handleSubmit}
        onCancel={() => setIsModalVisible(false)}
        isModalVisible={isModalVisible}
        setIsModalVisible={() => setIsModalVisible(!isModalVisible)}
      >
        <>
          <ModalNoteText as="h5">{pleaseNoteTranslation}:</ModalNoteText>
          <ModalDescriptionText as="p2">
            {updatingRiskAssessmentTranslation}
          </ModalDescriptionText>
          <ModalCarePlanList>
            {!!noCarePlansToDisplay &&
              carePlansToDisplay?.map((carePlan) => (
                <CarePlanItem key={carePlan.id}>
                  <ImageSection
                    popup
                    icon={carePlan?.icon}
                    iconColor={carePlan?.color}
                    iconText={carePlan?.iconText}
                  />
                  <CarePlanItemText as="h5">{carePlan?.name}</CarePlanItemText>
                </CarePlanItem>
              ))}
          </ModalCarePlanList>
        </>
      </Modal>
    )
  }

  const handleSave = useCallback(() => {
    const carePlansChoosen = carePlansState?.length
    const hasCarePlans = !!carePlansChoosen
    const hasFormChanges = !_.isEqual(fields, initial)

    if (
      (hasCarePlans && hasFormChanges) ||
      (hasChangedCarePlans() && hasCarePlans)
    ) {
      setIsModalVisible(true)
    } else {
      handleSubmit()
    }
  }, [fields, initial, carePlansState, hasChangedCarePlans, handleSubmit])
  if (!carePlanAdls?.length || !fields || !form || !resident) {
    return <Spinner />
  }

  return (
    <div
      data-testid="lmc-risk-assessment-form"
      id="lmc-risk-assessment-form"
      ref={formRef}
      style={style}
    >
      {renderHeader()}
      {renderFields()}
      {renderOutcomesScore()}
      {renderActionsSection()}
      {renderConsentSection()}
      {renderModal()}
      <RiskAssessmentEditFooter
        onCancel={onCancel}
        onSave={handleSave}
        isLoading={false}
        isEditing={!!riskAssessmentId}
        riskAssessmentId={riskAssessmentId}
        residentId={router.params.id}
        archived={archived}
      />
    </div>
  )
}

const FORM_ID = 'createRiskAssessment'
const getActionsNote = (
  carePlanNominative: string,
  t: (key: string, options?: Record<string, unknown>) => string
) =>
  t(riskAssessmentFormConstants.ACTIONS_NOTE, {
    carePlanNominative: getNominative(
      carePlanNominative,
      riskAssessmentFormConstants.CARE_PLAN
    )
  })

const mapStateToProps = (state: any, props: { router: RouterProps }) => {
  const residentId = props.router.params.id
  return {
    form: (
      Selectors.formatFieldsForAssessment as (
        state: any,
        props: { router: RouterProps }
      ) => FormTemplate
    )(state, props),
    resident: _.find(state.data.residents, { id: residentId }),
    carePlanAdls: state.data['care-plan-adls'],
    risk: (
      Selectors.calculateAssessmentRisk as (
        state: any,
        props: { router: RouterProps }
      ) => {
        level: 'na' | 'low' | 'medium' | 'high' | 'severe' | 'incomplete'
        score: number
      }
    )(state, props),
    carePlanNominative: state.global.home.preferredCarePlansNominative,
    carePlans: state.carePlans,
    features: state.global.home
  }
}

const mapDispatchToProps = (dispatch: Function) => ({
  fetchCarePlan: (id: string) => dispatch(Operations.fetchResidentAdls(id)),
  loadListItem: (list_id: string, item_id: string) =>
    dispatch(Operations.loadListItem(list_id, item_id)),
  addForm: (initial = {}) =>
    dispatch(
      Actions.addForm({
        formId: FORM_ID,
        initial
      })
    )
})

const wrappedForm = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(RefactoredRiskAssessmentForm)
)

export default Form(wrappedForm, FORM_ID)
